import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TaskType } from "@/types/user";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { DateTime } from "luxon";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "../ui/button";
import { TaskCard } from "./task-card";

const getCurrentWeekDays = (offset = 0) => {
    const startOfWeek = DateTime.now().setLocale("fr").startOf("week").plus({ weeks: offset }); // Monday in fr locale
    return Array.from({ length: 5 }, (_, i) => startOfWeek.plus({ days: i }));
};

const getTasksForDate = (tasks: TaskType[], date: DateTime) => {
    return tasks.filter(task => DateTime.fromISO(task.due_date ?? "").hasSame(date, "day"));
};

export const DataCalendar = ({ tasks, is_manager }: { tasks: TaskType[]; is_manager: boolean }) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [searchQuery, setSearchQuery] = useState<string | undefined>();
    const [weekOffset, setWeekOffset] = useState(0);
    const [orderBy, setOrderBy] = useState<string>(searchParams.get("o") ?? "priority");
    const weekDays = getCurrentWeekDays(weekOffset);

    useEffect(() => {
        const _weekDays = getCurrentWeekDays(weekOffset);
        searchParams.set("display", "calendar");
        searchParams.set(`due_date__range`, `${_weekDays[0].toFormat("yyyy-MM-dd")},${_weekDays[_weekDays.length - 1].toFormat("yyyy-MM-dd")}`);
        setSearchParams(searchParams);
    }, [weekOffset]);

    return (
        <div className="max-h-[520px] overflow-hidden">
            <div className="flex flex-row space-x-2 py-2">
                <Input
                    placeholder={"Search a task..."}
                    value={searchQuery}
                    onChange={event => {
                        setSearchQuery(event.target.value);
                    }}
                    className="max-w-sm"
                />
                <Select
                    value={orderBy}
                    onValueChange={value => {
                        setOrderBy(value);
                        searchParams.set("o", value);
                        setSearchParams(searchParams);
                    }}
                >
                    <SelectTrigger className="w-[220px]">
                        <SelectValue placeholder="Order by" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectGroup>
                            <SelectLabel>Order by</SelectLabel>
                            <SelectItem value="priority">Priority</SelectItem>
                            <SelectItem value="status">Status</SelectItem>
                            <SelectItem value="type">Type</SelectItem>
                        </SelectGroup>
                    </SelectContent>
                </Select>
                <div className="flex items-center space-x-2">
                    <div className="flex items-center space-x-1">
                        <Button size="icon" variant="ghost" onClick={() => setWeekOffset(prev => prev - 1)}>
                            <ChevronLeft />
                        </Button>
                        <Button size="icon" variant="ghost" onClick={() => setWeekOffset(prev => prev + 1)}>
                            <ChevronRight />
                        </Button>
                        <p>{weekDays[0].setLocale("fr").toFormat("LLLL yyyy")}</p>
                    </div>
                    <Button size="sm" variant="default" onClick={() => setWeekOffset(0)}>
                        Today
                    </Button>
                </div>
            </div>
            <div className="flex min-h-full min-w-full flex-1 flex-row">
                {weekDays.map((date, index) => (
                    <div key={date.toISODate()} className={`flex  w-1/5 flex-col px-2 ${index !== weekDays.length - 1 && "border-r-[1px]"}`}>
                        <h1 className="pb-4 font-bold">{date.setLocale("fr").toFormat("cccc dd LLL")}</h1>
                        <div className="h-[480px] space-y-2 overflow-scroll">
                            {getTasksForDate(tasks, date).map(task => {
                                return <TaskCard task={task} className="cursor-pointer" is_manager={is_manager} />;
                            })}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};
